import type { F0TimelineRowTaskProps } from '@factorialco/f0-react'
import {
  ArrowCycle,
  Check,
  Pencil,
  Sparkles,
  ThumbsUp,
} from '@factorialco/f0-react/icons/app'
import type { TimelineItemEmployeeFragment } from 'generated/resources/graphql'
import moment from 'moment'

import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { ApprovalsV2ApprovalTimelineEntry, TimelineGroup } from './workflowApprovalTypes'

export const calculateDaysOverdue = (dueOn: string | null | undefined): number => {
  if (!dueOn) return 0

  const deadlineDate = stringToDate(dueOn, 'backend')
  const daysOverdue = moment().startOf('day').diff(moment(deadlineDate).startOf('day'), 'days')

  return daysOverdue > 0 ? daysOverdue : 0
}

export const groupTimelineEntries = (entries: ApprovalsV2ApprovalTimelineEntry[]): TimelineGroup[] => {
  const groups: TimelineGroup[] = []
  const multitaskMap = new Map<string, ApprovalsV2ApprovalTimelineEntry[]>()

  for (const [, entry] of entries.entries()) {
    if (!entry.multitaskId) continue
    const existing = multitaskMap.get(entry.multitaskId) ?? []
    existing.push(entry)
    multitaskMap.set(entry.multitaskId, existing)
  }

  const emittedMultitasks = new Set<string>()

  for (const [i, entry] of entries.entries()) {
    if (entry.multitaskId) {
      if (emittedMultitasks.has(entry.multitaskId)) continue
      emittedMultitasks.add(entry.multitaskId)

      const collected = multitaskMap.get(entry.multitaskId)
      if (!collected) continue

      if (collected.length <= 1) {
        for (const e of collected) {
          groups.push({ kind: 'single', entry: e, index: i })
        }
      } else {
        groups.push({ kind: 'multi', entries: collected, index: i })
      }
    } else {
      groups.push({ kind: 'single', entry, index: i })
    }
  }

  return groups
}

export const mapTimelineStatus = (status: string): 'completed' | 'in-progress' | 'not-started' => {
  if (status === 'completed' || status === 'rejected') return 'completed'
  if (status === 'running' || status === 'changesrequested') return 'in-progress'
  return 'not-started'
}

export const getEntryIcon = (type: string, actionGroup: string | null | undefined) => {
  if (type === 'start') return Sparkles
  if (actionGroup === 'approvals') return ThumbsUp
  if (actionGroup === 'task') return Pencil
  return ArrowCycle
}

export const formatEntryDescription = (timestamp: string, status: string): string => {
  const date = stringToDate(timestamp, 'backend_datetime')
  const isCompleted = status === 'completed'
  const formattedDate = i18nDateToString(date, isCompleted ? 'ui_short_with_time' : 'ui')
  return isCompleted
    ? i18n.t('approvals_v2.timeline.completed_on', { date: formattedDate })
    : i18n.t('approvals_v2.timeline.estimated_on', { date: formattedDate })
}

export const resolveAvatar = (
  a: NonNullable<ApprovalsV2ApprovalTimelineEntry['assignees']>[number],
  employees: TimelineItemEmployeeFragment[]
): { type: 'team'; name: string } | { type: 'person'; firstName: string; lastName: string; src: string } | null => {
  if (a.peopleGroupName) {
    return {
      type: 'team' as const,
      name: a.peopleGroupName,
    }
  }
  const emp = employees.find((e) => String(e?.id) === String(a.employeeId))
  if (!emp) return null
  return {
    type: 'person' as const,
    firstName: emp.firstName || '',
    lastName: emp.lastName || '',
    src: buildUrl(emp.avatar?.url),
  }
}

export const avatarDisplayName = (avatar: NonNullable<ReturnType<typeof resolveAvatar>>) =>
  avatar.type === 'team'
    ? avatar.name
    : [avatar.firstName, avatar.lastName].filter(Boolean).join(' ')

export const buildMetadata = (
  entry: ApprovalsV2ApprovalTimelineEntry,
  employees: TimelineItemEmployeeFragment[]
): F0TimelineRowTaskProps['metadata'] => {
  const assignees = entry.assignees ?? []

  const unique = assignees.filter(
    (a, i, arr) =>
      arr.findIndex(
        (b) =>
          (b.employeeId != null && b.employeeId === a.employeeId) ||
          (b.peopleGroupName != null && b.peopleGroupName === a.peopleGroupName)
      ) === i
  )

  if (unique.length === 0) return undefined

  const isTerminal = entry.status === 'completed' || entry.status === 'rejected'

  if (isTerminal) {
    const completers = unique.filter((a) => a.completed)
    const rest = unique.filter((a) => !a.completed)

    // Completer(s) first — with green check badge and name
    const completerItems = completers
      .map((a) => {
        const avatar = resolveAvatar(a, employees)
        if (!avatar) return null
        return {
          ...avatar,
          badge: { type: 'positive' as const, icon: Check },
        }
      })
      .filter((a): a is NonNullable<typeof a> => a !== null)

    const metadata: NonNullable<F0TimelineRowTaskProps['metadata']> = []

    for (const c of completerItems) {
      metadata.push({
        label: 'Approver',
        hideLabel: true,
        value: {
          type: 'avatar' as const,
          variant: c,
          text: avatarDisplayName(c),
        },
      })
    }

    // Remaining assignees — compact list so metadata fits inline in the header
    const restAvatars = rest
      .map((a) => resolveAvatar(a, employees))
      .filter((a): a is NonNullable<typeof a> => a !== null)

    if (restAvatars.length > 0) {
      const teamAvatars = restAvatars.filter((a) => a.type === 'team')
      const personAvatars = restAvatars.filter((a) => a.type === 'person')

      for (const a of teamAvatars) {
        metadata.push({
          label: 'Assignee',
          hideLabel: true,
          value: { type: 'avatar' as const, variant: a, text: avatarDisplayName(a) },
        })
      }

      if (personAvatars.length > 0) {
        metadata.push({
          label: 'Assignees',
          hideLabel: true,
          value: { type: 'list' as const, variant: 'person' as const, avatars: personAvatars },
        })
      }
    }

    return metadata.length > 0 ? metadata : undefined
  }

  // Non-terminal entries: show all assignees
  const avatars = unique
    .map((a) => resolveAvatar(a, employees))
    .filter((a): a is NonNullable<typeof a> => a !== null)

  if (avatars.length === 0) return undefined

  const teamAvatars = avatars.filter((a) => a.type === 'team')
  const personAvatars = avatars.filter((a) => a.type === 'person')

  const items: NonNullable<F0TimelineRowTaskProps['metadata']> = []

  for (const a of teamAvatars) {
    items.push({
      label: 'Assignee',
      hideLabel: true,
      value: { type: 'avatar' as const, variant: a, text: avatarDisplayName(a) },
    })
  }

  if (personAvatars.length <= 3) {
    for (const a of personAvatars) {
      items.push({
        label: 'Assignee',
        hideLabel: true,
        value: { type: 'avatar' as const, variant: a, text: avatarDisplayName(a) },
      })
    }
  } else {
    items.push({
      label: 'Assignees',
      hideLabel: true,
      value: { type: 'list' as const, variant: 'person' as const, avatars: personAvatars },
    })
  }

  return items.length > 0 ? items : undefined
}
